import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  AccountHero,
  DesignCard,
  EmptyState,
  OrderRow,
  SectionHead,
} from "@/components/account/AccountParts"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import type { AccountOverviewProps, PageProps } from "@/types/inertia"
import "@/styles/public-account.css"

const TITLE = "My account"
const DESCRIPTION =
  "Your SOH GD Wines account: the label designs you have made in our studio, and every order and enquiry you have sent us, with where each one is now."

// The account overview (/account, M6 decision 14): a greeting, the
// verification banner until the email is verified, the three newest designs
// and orders with "See all", and the account's details, password and sign-out.
export default function Account() {
  const { props } = usePage<PageProps<AccountOverviewProps>>()
  const { account, designs, design_count: designCount, orders, order_count: orderCount } = props

  return (
    <PublicShell>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>

      <AccountHero
        titleId="account-title"
        eyebrow="My account"
        title={
          <>
            Welcome, <span className="soft">{account.display_name}</span>
          </>
        }
        lede="Your label designs and your orders with us, in one place."
      />

      <section className="band band-charcoal acct-band" aria-label="Your account">
        <div className="shell">
          {!account.email_verified && <VerifyBanner email={account.email} />}

          <section className="acct-section" aria-labelledby="account-designs-title">
            <SectionHead
              id="account-designs-title"
              title="My designs"
              count={designCount}
              seeAll={designCount > 0 ? { href: "/account/designs", label: "designs" } : undefined}
            />
            {designs.length > 0 ? (
              <div className="acct-designs">
                {designs.map((design) => (
                  <DesignCard key={design.id} design={design} />
                ))}
              </div>
            ) : (
              <EmptyState
                eyebrow="Nothing designed yet"
                title="No designs yet"
                action={{ href: "/studio", label: "Design a label" }}
              >
                Start from a template or bring your own artwork, and see it on the bottle. Every design
                you save or download waits for you here.
              </EmptyState>
            )}
          </section>

          <section className="acct-section" aria-labelledby="account-orders-title">
            <SectionHead
              id="account-orders-title"
              title="My orders"
              count={orderCount}
              seeAll={orderCount > 0 ? { href: "/account/orders", label: "orders" } : undefined}
            />
            {orders.length > 0 ? (
              <ul className="acct-orders">
                {orders.map((order) => (
                  <OrderRow key={order.id} order={order} />
                ))}
              </ul>
            ) : (
              <EmptyState
                eyebrow="Nothing sent yet"
                title="No orders yet"
                action={{ href: "/wines", label: "Browse our wines" }}
              >
                Choose your wines, mixed as you like from 100 bottles, and send us an order enquiry. You
                can follow every step from here.
              </EmptyState>
            )}
          </section>

          <section className="acct-section" aria-labelledby="account-details-title">
            <SectionHead id="account-details-title" title="Your details" />
            <AccountDetails account={account} />
          </section>
        </div>
      </section>
    </PublicShell>
  )
}

function VerifyBanner({ email }: { email: string }) {
  const [sending, setSending] = React.useState(false)

  const resend = () => {
    if (sending) return
    router.post("/verify-email", {}, {
      preserveScroll: true,
      onStart: () => setSending(true),
      onFinish: () => setSending(false),
    })
  }

  return (
    <div className="acct-verify" role="region" aria-labelledby="verify-title">
      <div>
        <h2 id="verify-title">Verify your email to see enquiries sent before you signed up</h2>
        <p>
          We sent a link to {email}. Open it, and anything you sent us from this address before you had
          an account appears here. The link works for two days.
        </p>
      </div>
      <Button type="button" variant="ghost-hairline" size="sm" onClick={resend} disabled={sending}>
        {sending ? "Sending…" : "Send the link again"}
      </Button>
    </div>
  )
}

function AccountDetails({ account }: { account: AccountOverviewProps["account"] }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-20">
      <dl className="acct-facts">
        <div>
          <dt>Name</dt>
          <dd>{account.name ?? "Not given"}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>
            {account.email}
            <span className="acct-meta">{account.email_verified ? "Verified" : "Not verified yet"}</span>
          </dd>
        </div>
      </dl>
      <div className="acct-links">
        <Link href="/profile" className="acct-action">
          Change your email
        </Link>
        <Link href="/profile/password" className="acct-action">
          Change your password
        </Link>
        <button type="button" className="acct-action" onClick={() => router.delete("/logout")}>
          Sign out
        </button>
      </div>
    </div>
  )
}
